import React, { FC, useCallback, useRef } from 'react';
import { TextField } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useGetScalingSize } from '@ui/Report';
import { REPORT_BODY_TEXT } from './editable-page-header.constants';
import { useReportEditableFieldDisplay } from './use-report-editable-field-display.hook';

const DEFAULT_MAX_ROWS = 20;

/**
 * One row's worth of height as a scaling number, derived from Figma body-text
 * spec. Used for the CSS `max-height` guard since MUI's `TextareaAutosize`
 * shadow element lives outside the container-query context and cannot resolve
 * the scaled font size — making the built-in `maxRows` cap unreliable.
 */
const ROW_HEIGHT = REPORT_BODY_TEXT.fontSize * REPORT_BODY_TEXT.lineHeight;

type ReportBodyTextFieldProps = {
  value: string;
  onChange: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onBlur?: () => void;
  placeholder?: string;
  /**
   * Hard row cap — textarea grows to this height, then blocks further input.
   * Defaults to {@link DEFAULT_MAX_ROWS}.
   */
  maxRows?: number;
  /** Max width in cqw (pass {@link figmaPxToWidthPercent} for Figma px). */
  maxWidth?: number;
  fullWidth?: boolean;
};

/**
 * Inline-editable report body copy — Figma node 4767:41121.
 * Shared by proposal dashboard, summary columns, and other report pages.
 *
 * The field grows to {@link maxRows} rows then hard-blocks further input.
 * All entered text is visible at all times — no internal scroll.
 *
 * Two guards enforce the row cap:
 * 1. CSS `max-height` on the textarea — clips display regardless of how MUI
 *    measures height (MUI's shadow element is outside the cqw context).
 * 2. `onChange` / `onKeyDown` interceptors — block input that would overflow
 *    the cap; the native textarea value is imperatively reverted on overflow.
 */
export const ReportBodyTextField: FC<ReportBodyTextFieldProps> = ({
  value,
  onChange,
  onBlur,
  placeholder,
  maxRows = DEFAULT_MAX_ROWS,
  maxWidth,
  fullWidth = true,
}) => {
  const getScalingSize = useGetScalingSize();
  const { classes } = useStyles({ getScalingSize, maxWidth, maxRows });
  const { displayValue, activePlaceholder, bindFocusHandlers } =
    useReportEditableFieldDisplay(value, placeholder);

  /**
   * Track the last accepted value so we can imperatively revert the native
   * textarea when a change would overflow the max-height constraint.
   * React controlled inputs don't reset the DOM value unless state changes,
   * so we must do it manually here.
   */
  const acceptedValueRef = useRef(displayValue);
  acceptedValueRef.current = displayValue;

  /**
   * Resolves the computed line height in px from the textarea element.
   * Falls back to the Figma-spec value (converted to px via the current
   * font size) when `getComputedStyle` returns `"normal"` — which can
   * happen if CSS inheritance breaks between the MUI wrapper and the
   * native textarea. The CSS `max-height` guard is the primary display
   * cap; these JS guards are a secondary layer for the controlled value.
   */
  const getLineHeightPx = useCallback((el: HTMLTextAreaElement): number => {
    const computed = parseFloat(getComputedStyle(el).lineHeight);
    if (!isNaN(computed)) return computed;
    const fontSizePx = parseFloat(getComputedStyle(el).fontSize);
    return (isNaN(fontSizePx) ? 16 : fontSizePx) * REPORT_BODY_TEXT.lineHeight;
  }, []);

  /** True when the element's content strictly exceeds the row cap (post-change). */
  const isOverflowing = useCallback(
    (el: HTMLTextAreaElement): boolean =>
      el.scrollHeight > getLineHeightPx(el) * maxRows,
    [getLineHeightPx, maxRows],
  );

  /** True when the element is at or beyond the row cap (pre-keypress check). */
  const isAtOrOverLimit = useCallback(
    (el: HTMLTextAreaElement): boolean =>
      el.scrollHeight >= getLineHeightPx(el) * maxRows,
    [getLineHeightPx, maxRows],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      // Only block printable characters and Enter — allow navigation, delete, etc.
      const isModified = e.ctrlKey || e.metaKey || e.altKey;
      const isPrintableOrEnter = e.key === 'Enter' || e.key.length === 1;
      if (!isPrintableOrEnter || isModified) return;
      // Use >= so a textarea already at the limit blocks the next character
      // immediately rather than letting it through to onChange for reversion.
      if (isAtOrOverLimit(e.currentTarget)) e.preventDefault();
    },
    [isAtOrOverLimit],
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const isAdding = e.target.value.length > acceptedValueRef.current.length;
      if (isAdding && isOverflowing(e.target)) {
        // Revert the native textarea value — React won't do this automatically
        // because there is no state change to trigger a reconciliation.
        e.target.value = acceptedValueRef.current;
        return;
      }
      onChange(e);
    },
    [isOverflowing, onChange],
  );

  return (
    <TextField
      multiline
      minRows={1}
      maxRows={maxRows}
      fullWidth={fullWidth}
      variant="standard"
      autoComplete="off"
      placeholder={activePlaceholder}
      value={displayValue}
      onChange={handleChange}
      className={classes.root}
      slotProps={{
        htmlInput: {
          autoComplete: 'off',
          onKeyDown: handleKeyDown,
          style: { overflow: 'hidden' },
        },
        input: {
          disableUnderline: true,
          className: classes.input,
          ...bindFocusHandlers(onBlur),
        },
      }}
    />
  );
};

const useStyles = makeStyles<{
  getScalingSize: ReturnType<typeof useGetScalingSize>;
  maxWidth?: number;
  maxRows: number;
}>()((_theme, { getScalingSize, maxWidth, maxRows }) => ({
  root: {
    position: 'relative',
    zIndex: 1,
    overflow: 'visible',
    ...(maxWidth !== undefined && {
      maxWidth: getScalingSize(maxWidth),
    }),
  },
  input: {
    padding: 0,
    overflow: 'visible',
    fontFamily: REPORT_BODY_TEXT.fontFamily,
    fontSize: getScalingSize(REPORT_BODY_TEXT.fontSize),
    fontWeight: REPORT_BODY_TEXT.fontWeight,
    lineHeight: REPORT_BODY_TEXT.lineHeight,
    color: REPORT_BODY_TEXT.color,
    textAlign: REPORT_BODY_TEXT.textAlign,
    wordBreak: 'break-word',
    alignItems: 'flex-start',
    '& textarea': {
      overflow: 'hidden !important',
      resize: 'none',
      // CSS max-height as a hard display cap. MUI's TextareaAutosize measures
      // height using a shadow element appended to <body> — outside the
      // container-query context — so cqw-based font sizes resolve incorrectly
      // there. This rule enforces the cap directly in the layout engine.
      maxHeight: getScalingSize(ROW_HEIGHT * maxRows),
    },
    '&::placeholder': {
      color: REPORT_BODY_TEXT.color,
      opacity: 0.5,
    },
  },
}));
